import userService from "@/services/user.service";
import { Button, Card, Input, message, Divider, Typography, Space, Badge, List, Tabs, Modal, InputNumber } from "antd";
import React, { useEffect, useState } from "react";
import {
  CopyOutlined,
  ShareAltOutlined,
  UserAddOutlined,
  GiftOutlined,
  FileTextOutlined,
  UserOutlined,
  LockOutlined,
  PlusOutlined,
  DollarOutlined,
  DeploymentUnitOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import { useSelector } from "react-redux";
import InspectionBtn from "@/components/InspectionBtn";
import toast from "react-hot-toast";
import { useAuth } from "@/hooks/useAuth";

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const UserProfile = ({ subscription, menuname }) => {
  // Mock user data - replace with actual API data
  const [userInfo, setUserInfo] = useState({
    username: '',
    email: '',
    designation: '',
    referralCode: '',
    totalReferrals: 0,
    referralRewards: 0,
    reportPermissions: 10, // Default 10 report permissions
    usedReports: 0
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const { logout, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(false);
  const [referralHistory, setReferralHistory] = useState([]);
  const [showReferralHistory, setShowReferralHistory] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showBuyReportsModal, setShowBuyReportsModal] = useState(false);
  const [pricePerReport] = useState(5); // $5 per report

  useEffect(() => {
    const getUserProfileInfo = async () => {
      try {
        const userinfo = await userService.getuserprofile();
        setUserInfo({
          ...userinfo.data,
          // Mock referral data - replace with actual API data
          referralCode: userinfo.data.referralCode || generateReferralCode(userinfo.data.username),
          totalReferrals: userinfo.data.totalReferrals || 0,
          referralRewards: userinfo.data.referralRewards || 0,
          reportPermissions: userinfo.data.reportPermissions || 10,
          usedReports: userinfo.data.usedReports || 0
        });

        // Fetch referral history
      } catch (error) {
        toast.error('Failed to load profile information');
      }
    };
    getUserProfileInfo();
  }, []);

  const generateReferralCode = (username) => {
    return `${username.toUpperCase()}${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  };



  const handleEdit = () => {
    setEditForm({ ...userInfo });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditForm({ ...userInfo });
    setIsEditing(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await userService.updateuserProfile(editForm);
      setUserInfo({ ...editForm });
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match!');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long!');
      return;
    }

    setLoading(true);
    try {
      // Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordForm(false);
      toast.success('Password updated successfully!');
    } catch (error) {
      toast.error('Failed to update password');
    } finally {
      setLoading(false);
    }
  };
   const shareReferralLink = async () => {
    const encodeId = (id) => btoa(id);
    const referralLink = `${window.location.origin}/register?ref=${encodeId(userInfo.owner_id)}`;
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Referral link copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy referral link");
    }
  };



  const handleBuyReports = async () => {
    try {
      setShowBuyReportsModal(true);
    } catch (error) {
      toast.error('Failed to purchase reports');
    } finally {
      setLoading(false);
    }
  };

  const remainingReports = userInfo.reportPermissions - userInfo.usedReports;

  const handleLogout = async () => {
    try {
      await logout();
      window.location.reload();
      navigate("/login");

    } catch (error) {
      console.error("Logout failed:", error);
    }
  };
  // Profile Tab Content
  const ProfileContent = () => (
    <Card className="shadow-md">
      <div className="space-y-4">
        {/* Avatar Section */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex">
            <div className="w-16 h-16 flash-bgcolor rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {userInfo.username.charAt(0).toUpperCase()}
            </div>
            <div className="mt-3 ms-3">
              <h2 className="text-xl font-semibold text-gray-900">{userInfo.username}</h2>
              <p className="text-gray-600">{userInfo.designation}</p>
            </div>
          </div>
          <div className="space-x-3">
            <InspectionBtn onClick={handleBuyReports} size="small">Upgrade</InspectionBtn>
            <InspectionBtn size="small"
              onClick={() => {
                handleLogout();
              }}
              icon={<LogoutOutlined />
              }
            >
              <span>Logout</span>
            </InspectionBtn></div>
        </div>


        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
            <Input
              value={isEditing ? editForm.username : userInfo.username}
              onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              disabled={!isEditing}
              placeholder="Enter username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <Input
              value={isEditing ? editForm.email : userInfo.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              disabled={!isEditing}
              placeholder="Enter email"
              type="email"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
            <Input
              value={isEditing ? editForm.designation : userInfo.designation}
              onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })}
              disabled={!isEditing}
              placeholder="Enter designation"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-start items-center pt-4 border-t border-gray-200">
          {isEditing ? (
            <div className="space-x-2">
              <InspectionBtn type="primary" onClick={handleSave} loading={loading}>
                Save Changes
              </InspectionBtn>
              <InspectionBtn variant="secondary" onClick={handleCancel}>
                Cancel
              </InspectionBtn>
            </div>
          ) : (
            <InspectionBtn type="primary" onClick={handleEdit}>
              Edit Profile
            </InspectionBtn>
          )}
        </div>
      </div>
    </Card>
  );

  // Password Tab Content
  const PasswordContent = () => (
    <Card className="shadow-md">
      <div className="space-y-4 max-w-md">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
          <Input
            type="password"
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
            placeholder="Enter current password"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
          <Input
            type="password"
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            placeholder="Enter new password"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
          <Input
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
            placeholder="Confirm new password"
          />
        </div>

        <div className="flex space-x-2 pt-4">
          <InspectionBtn type="primary" onClick={handlePasswordUpdate} loading={loading}>
            Update Password
          </InspectionBtn>
          <InspectionBtn variant="secondary" onClick={() => {
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
          }}>
            Clear
          </InspectionBtn>
        </div>
      </div>
    </Card>
  );

  // Referral Tab Content
  const ReferralContent = () => (
    <Card className="shadow-md">
      <div className="space-y-6">


        {/* Referral Code Section */}
        <div className="space-y-4">
          <Title level={5}>Your Referral Code</Title>
          <div className="flex items-center space-x-3">

            <InspectionBtn
              icon={<ShareAltOutlined />}
              onClick={shareReferralLink}
              type="primary"
              title="Copy referral link"
            >
              Share Link
            </InspectionBtn>

          </div>
          <Text type="secondary" className="text-sm">
            Share this code with friends to earn rewards when they sign up!
          </Text>
        </div>

        <Divider />

        {/* Referral Instructions */}
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-start space-x-3">
            <GiftOutlined className="text-green-600 mt-1" />
            <div>
              <Title level={5} className="mb-2">How it works:</Title>
              <ul className="text-sm space-y-1 text-gray-700">
                <li>• Share your referral code or link with friends</li>
                <li>• They sign up using your code</li>
                <li>• Both of you earn 10 report creation</li>
              </ul>
            </div>
          </div>
        </div>


      </div>
    </Card>
  );

  return (
    <div className=" mx-auto px-6 py-2">


      {menuname == "profile" && <ProfileContent />}
      {menuname == "password" && <PasswordContent />}
      {menuname == "refer" && <ReferralContent />}

      <Modal
        title={
          <div className="flex items-center space-x-2">
            <DollarOutlined className="text-green-600" />
            <span>Plan Details</span>
          </div>
        }
        open={showBuyReportsModal}
        onCancel={() => setShowBuyReportsModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowBuyReportsModal(false)}>
            Close
          </Button>
        ]}
      >
        {/* Info message */}
        <div className="bg-yellow-50 p-3 rounded-md text-sm text-gray-700 mb-4">
          Please contact <strong>Admin</strong> for further details to change or upgrade your plan.
        </div>

        {/* Plan details card */}
        <div className="bg-white border rounded-lg shadow-sm p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Your Plan Details</h4>

          <div className="grid grid-cols-2 gap-y-2 text-xs text-gray-600">
            <div className="font-medium">Plan:</div>
            <div>{subscription?.name}</div>

            <div className="font-medium">Max Users:</div>
            <div>{subscription?.max_users}</div>

            <div className="font-medium">Max Ships:</div>
            <div>{subscription?.max_ships}</div>

            <div className="font-medium">Max Events:</div>
            <div>{subscription?.max_events}</div>

            <div className="font-medium">Reports:</div>
            <div>{subscription?.no_of_reports}</div>

            <div className="font-medium">Expiry:</div>
            <div>{subscription?.expiry_date ? new Date(subscription?.expiry_date).toLocaleDateString() : "-"}</div>
          </div>
        </div>
      </Modal>


    </div>
  );
};

export default UserProfile;